import { CodeEditor } from "features/uiBuilder";
import StyledDrawer, {
  StyledDrawerContainer,
  StyledDrawerTitle,
} from "Components/UI/StyledDrawer";
import { StyledAllotmentWrapper } from "features/uiBuilder/components/CodeComponentBuilder";
import { Allotment, AllotmentHandle } from "allotment";
import { Typography } from "@mui/material";
import { useCallback, useRef } from "react";
import { CodeEditorHandle } from "features/uiBuilder/components/CodeEditor";
import { useEditor } from "@grapesjs/react";
import { AnimatePresence, motion } from "motion/react";

interface LightningLanderBuilderCodeDrawerProps {
  open: boolean;
  onClose: () => void;
  htmlContent: string;
  cssContent: string;
  jsContent: string;
  onHtmlChange: (v: string) => void;
  onCssChange: (v: string) => void;
  onJsChange: (v: string) => void;
}

function LightningLanderBuilderCodeDrawer({
  open,
  onClose,
  htmlContent,
  cssContent,
  jsContent,
  onHtmlChange,
  onCssChange,
  onJsChange,
}: LightningLanderBuilderCodeDrawerProps) {
  const htmlEditorRef = useRef<CodeEditorHandle>(null);
  const cssEditorRef = useRef<CodeEditorHandle>(null);
  const jsEditorRef = useRef<CodeEditorHandle>(null);
  const allotmentRef = useRef<AllotmentHandle>(null);
  const editor = useEditor();

  const onAnimationComplete = useCallback(() => {
    allotmentRef.current?.reset();
    htmlEditorRef.current?.beautifyCode();
    cssEditorRef.current?.beautifyCode();
    jsEditorRef.current?.beautifyCode();
  }, []);


  // Handler to remove unused CSS properties using GrapesJS
  const handleRemoveUnusedCss = () => {
    if (!editor) return;
    // Get only used CSS (removes unused properties)
    const usedCss =
      editor.getCss({
        avoidProtected: true,
        keepUnusedStyles: false,
      }) || "";
    onCssChange(usedCss);
    setTimeout(() => {
      cssEditorRef.current?.beautifyCode();
    }, 1);
  };

  return (
    <StyledDrawer open={open} onClose={onClose}>
      <StyledDrawerTitle>
        <Typography variant="h6">Edit HTML/CSS/JS</Typography>
        <Typography variant="subtitle2" color="textSecondary">
          Close drawer to save changes
        </Typography>
      </StyledDrawerTitle>
      <StyledDrawerContainer
        width={"calc(100vw - 80px)"}
        sx={{ height: "calc(100% - 85px)" }}
      >
        <AnimatePresence mode="wait">

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ height: "100%" }}
            onAnimationComplete={onAnimationComplete}
          >
            <StyledAllotmentWrapper ref={allotmentRef}>

              <Allotment.Pane minSize={300} >
                <CodeEditor
                  mode="html"
                  title="HTML (body content only)"
                  onChange={onHtmlChange}
                  value={htmlContent}
                  ref={htmlEditorRef}
                />

              </Allotment.Pane>
              <Allotment.Pane minSize={300}>
                <CodeEditor
                  mode="css"
                  title="CSS"
                  onChange={onCssChange}
                  value={cssContent}
                  ref={cssEditorRef}
                  // Add a custom action button for removing unused CSS
                  actionButton={{
                    label: "Remove Unused",
                    onClick: handleRemoveUnusedCss,
                  }}
                />

              </Allotment.Pane>
              <Allotment.Pane minSize={300}>
                <CodeEditor
                  mode="javascript"
                  title="JS"
                  onChange={onJsChange}
                  value={jsContent}
                  ref={jsEditorRef}
                />

              </Allotment.Pane>

            </StyledAllotmentWrapper>
          </motion.div>
        </AnimatePresence>
      </StyledDrawerContainer>
    </StyledDrawer>
  );
}

export default LightningLanderBuilderCodeDrawer;
